import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import "bootstrap-icons/font/bootstrap-icons.css";
import logo from "../assets/logohorizental.png";
import { CourseHighlightCard } from "../components/CourseCatalog";
import { buildMarksSummary, buildModuleDetailsRows, fetchAmciDesign, fetchGradePoints, printAmciDocument, resolveGradePointByPercentage, resolveStudentPhoto } from "../utils/amciDesign";
import DirectChat from "../components/DirectChat";
import { sweetAlert } from "../utils/sweetAlerts";
import NotificationBell from "../components/NotificationBell";

const API = "/api";

const menuItems = [
  { icon: "bi-house", label: "Dashboard", key: "dashboard" },
  {
    icon: "bi-download",
    label: "Download",
    children: [{ label: "Study Material", key: "study-material" }],
  },
  {
    icon: "bi-file-earmark-text",
    label: "Examination",
    children: [
      { label: "Admit Card", key: "admit-card" },
      { label: "Online Exam", key: "online-exam" },
      { label: "Exam Result", key: "exam-result" },
    ],
  },
  {
    icon: "bi-award",
    label: "Certification",
    children: [
      { label: "Certificate Download", key: "certificate-download" },
      { label: "Marksheet Download", key: "marksheet-download" },
    ],
  },
  { icon: "bi-trophy", label: "Achievement", key: "achievement" },
  {
    icon: "bi-chat-dots",
    label: "Feedback Box",
    children: [
      { label: "Feedback Box", key: "send-feedback" },
      { label: "Complaint Box", key: "send-complain" },
    ],
  },
  {
    icon: "bi-chat-left-text",
    label: "Message",
    children: [
      { label: "New Message", key: "new-message" },
      { label: "Feedback Box", key: "student-feedback-box" },
    ],
  },
  {
    icon: "bi-gear",
    label: "Profile Settings",
    children: [
      { label: "Profile Edit Some Data", key: "profile-edit" },
      { label: "Change Password", key: "change-password" },
      { label: "ID Card Download", key: "id-card-download" },
    ],
  },
];

const shellStyle = {
  display: "flex",
  minHeight: "100vh",
  background: "radial-gradient(circle at top left, #dbeafe 0%, #f8fafc 34%, #fff7ed 100%)",
};

const pageCardStyle = {
  background: "rgba(255,255,255,0.94)",
  borderRadius: "24px",
  border: "1px solid rgba(148, 163, 184, 0.16)",
  boxShadow: "0 18px 36px rgba(15, 23, 42, 0.08)",
};

const panelStyle = {
  ...pageCardStyle,
  padding: "22px",
};

const detailRowBaseStyle = {
  display: "grid",
  gap: "10px",
  padding: "10px 0",
  borderBottom: "1px solid rgba(148, 163, 184, 0.14)",
};

const statCardStyle = {
  ...pageCardStyle,
  padding: "18px 20px",
};

function toDateInputValue(value) {
  if (!value) return "";
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
  const match = /^(\d{2})-(\d{2})-(\d{4})$/.exec(value);
  if (!match) return "";
  return `${match[3]}-${match[2]}-${match[1]}`;
}

function DetailRow({ label, value, compact = false }) {
  return (
    <div style={{ ...detailRowBaseStyle, gridTemplateColumns: compact ? "1fr" : "160px minmax(0, 1fr)" }}>
      <strong style={{ color: "#475569" }}>{label}</strong>
      <span style={{ color: "#0f172a", wordBreak: "break-word" }}>{value || "-"}</span>
    </div>
  );
}

function StatCard({ label, value, icon, helper }) {
  return (
    <div style={statCardStyle}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: "14px", alignItems: "center" }}>
        <div>
          <p style={{ margin: 0, color: "#64748b", fontWeight: 700 }}>{label}</p>
          <h3 style={{ margin: "10px 0 6px", color: "#0f172a" }}>{value}</h3>
          <p style={{ margin: 0, color: "#94a3b8", fontSize: "13px", lineHeight: 1.5 }}>{helper}</p>
        </div>
        <div
          style={{
            width: "50px",
            height: "50px",
            borderRadius: "18px",
            background: "linear-gradient(135deg, #7c2d3a 0%, #c2410c 100%)",
            color: "#fff",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "20px",
            flexShrink: 0,
            boxShadow: "0 16px 28px rgba(124, 45, 58, 0.24)",
          }}
        >
          <i className={`bi ${icon}`}></i>
        </div>
      </div>
    </div>
  );
}

function SectionHeader({ title, description, action }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", gap: "16px", alignItems: "flex-start", flexWrap: "wrap", marginBottom: "16px" }}>
      <div>
        <h3 style={{ margin: 0, color: "#0f172a" }}>{title}</h3>
        <p style={{ margin: "6px 0 0", color: "#64748b", lineHeight: 1.65 }}>{description}</p>
      </div>
      {action || null}
    </div>
  );
}

function StudentDashboard() {
  const navigate = useNavigate();
  const student = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("student") || "null");
    } catch (error) {
      return null;
    }
  }, []);

  const initialDashboardData = useMemo(() => {
    const studentName = student?.student_name || "Student";
    const registrationNo = student?.registration_no || student?.login_id || "";
    return {
      summary: {
        student_name: studentName,
        registration_no: registrationNo,
        course_name: "Loading latest data...",
        course_duration: "-",
        course_semester_count: 1,
        session_year: "Loading...",
        study_centre_name: "Loading...",
        study_centre_login: "-",
        study_centre_contact: "-",
        status: "approved",
        marks_count: 0,
        approved_marks_count: 0,
        pending_marks_count: 0,
        average_percentage: 0,
        document_count: 0,
      },
      profile: {
        id: student?.id,
        student_name: studentName,
        registration_no: registrationNo,
        phone: "",
        email: "",
        guardian_name: "",
        address: "",
      },
      marks: [],
      documents: [],
    };
  }, [student]);

  const [data, setData] = useState(initialDashboardData);
  const [loading, setLoading] = useState(() => !student?.id);
  const [refreshingDashboard, setRefreshingDashboard] = useState(false);
  const [error, setError] = useState("");
  const [viewportWidth, setViewportWidth] = useState(() => (typeof window === "undefined" ? 1440 : window.innerWidth));
  const [mobileMenu, setMobileMenu] = useState(false);
  const [openMenus, setOpenMenus] = useState({
    Download: false,
    Examination: false,
    Certification: false,
    "Feedback Box": false,
    Message: false,
    "Profile Settings": false,
  });
  const [activeSection, setActiveSection] = useState("dashboard");
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [feedbackForm, setFeedbackForm] = useState({ type: "Feedback", message: "" });
  const [studentFeedbackForm, setStudentFeedbackForm] = useState({ rating: "5", feedback: "" });
  const [studentFeedbackRows, setStudentFeedbackRows] = useState([]);
  const [profileForm, setProfileForm] = useState({
    student_name: "",
    phone: "",
    email: "",
    guardian_name: "",
    address: "",
  });
  const [passwordForm, setPasswordForm] = useState({ current_password: "", new_password: "", new_password_confirmation: "" });
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);
  const [submittingMessage, setSubmittingMessage] = useState(false);
  const [submittingStudentFeedback, setSubmittingStudentFeedback] = useState(false);
  const [loadingStudentFeedbacks, setLoadingStudentFeedbacks] = useState(false);
  const [toast, setToast] = useState("");
  const profileMenuRef = useRef(null);

  const isMobile = viewportWidth <= 768;
  const isTablet = viewportWidth <= 1080;

  useEffect(() => {
    const handleResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (!student?.id) {
      navigate("/student-login", { replace: true });
      return;
    }

    let active = true;

    const loadDashboard = async () => {
      try {
        setRefreshingDashboard(true);
        setError("");
        const res = await axios.get(`${API}/student-portal/${student.id}/dashboard`, {
          params: { _ts: Date.now() },
          timeout: 12000,
        });
        if (!active) return;
        const nextData = {
          summary: res.data?.summary || {},
          profile: res.data?.profile || null,
          marks: res.data?.marks || [],
          documents: res.data?.documents || [],
        };
        setData(nextData);
        setProfileForm({
          student_name: nextData.profile?.student_name || "",
          phone: nextData.profile?.phone || "",
          email: nextData.profile?.email || "",
          guardian_name: nextData.profile?.guardian_name || "",
          address: nextData.profile?.address || "",
        });
      } catch (err) {
        if (!active) return;
        setError(err.code === "ECONNABORTED" ? "Student dashboard data load is taking too long. Please refresh once." : err.response?.data?.message || "Student dashboard load failed.");
      } finally {
        if (active) {
          setLoading(false);
          setRefreshingDashboard(false);
        }
      }
    };

    loadDashboard();
    return () => {
      active = false;
    };
  }, [navigate, student?.id]);

  useEffect(() => {
    if (!toast) return undefined;
    const timer = setTimeout(() => setToast(""), 2800);
    return () => clearTimeout(timer);
  }, [toast]);

  useEffect(() => {
    const handleOutside = (event) => {
      if (profileMenuRef.current && !profileMenuRef.current.contains(event.target)) {
        setShowProfileMenu(false);
      }
    };
    document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, []);

  useEffect(() => {
    if (!isMobile) {
      setMobileMenu(false);
    }
  }, [isMobile]);

  const summary = data.summary || {};
  const profile = data.profile || {};
  const marks = data.marks || [];
  const documents = data.documents || [];
  const approvedMarks = marks.filter((row) => row.approval_status === "approved");
  const marksSource = approvedMarks.length > 0 ? approvedMarks : marks;
  const totalObtained = marksSource.reduce((sum, row) => sum + Number(row.marks_obtained || 0), 0);
  const totalMax = marksSource.reduce((sum, row) => sum + Number(row.max_marks || 0), 0);
  const averagePercentage = totalMax > 0 ? ((totalObtained / totalMax) * 100).toFixed(2) : "0.00";
  const achievements = [
    {
      label: "Approved Student",
      helper: "Your admission has been approved by the institute.",
      ready: summary.status === "approved",
      icon: "bi-patch-check-fill",
    },
    {
      label: "Course Enrolled",
      helper: summary.course_name || "Course not assigned yet.",
      ready: Boolean(summary.course_name && summary.course_name !== "Not Assigned"),
      icon: "bi-journal-bookmark-fill",
    },
    {
      label: "Marks Progress",
      helper: `${marks.length} module record available in the panel.`,
      ready: marks.length > 0,
      icon: "bi-bar-chart-fill",
    },
    {
      label: "Documents Ready",
      helper: `${documents.length} profile files available for review.`,
      ready: documents.length > 0,
      icon: "bi-folder-check",
    },
  ];

  const logout = () => {
    localStorage.removeItem("student");
    localStorage.removeItem("studentLastActiveAt");
    navigate("/student-login");
  };

  const refreshData = async () => {
    window.location.reload();
  };

  const openSection = (key) => {
    setActiveSection(key);
    if (isMobile) setMobileMenu(false);
  };

  const toggleMenu = (label) => {
    setOpenMenus((prev) => ({ ...prev, [label]: !prev[label] }));
  };

  const submitMessage = async (type) => {
    if (!feedbackForm.message.trim()) {
      setToast(`${type} message লিখতে হবে`);
      return;
    }

    try {
      setSubmittingMessage(true);
      await axios.post(`${API}/contact`, {
        name: profile.student_name || student?.student_name || "Student",
        email: profile.email || "student@nidcs.org",
        message: `[${type}] Reg: ${summary.registration_no || "-"} | ${feedbackForm.message.trim()}`,
      });
      setFeedbackForm({ type, message: "" });
      setToast(`${type} sent successfully`);
    } catch (err) {
      setToast(err.response?.data?.message || `${type} send failed`);
    } finally {
      setSubmittingMessage(false);
    }
  };

  const submitStudentFeedback = async () => {
    if (!studentFeedbackForm.rating) {
      await sweetAlert("Please select rating", { icon: "warning" });
      return;
    }

    if (!studentFeedbackForm.feedback.trim()) {
      await sweetAlert("Please write feedback description", { icon: "warning" });
      return;
    }

    try {
      setSubmittingStudentFeedback(true);
      const res = await axios.post(`${API}/student-feedbacks`, {
        student_id: student?.id,
        rating: Number(studentFeedbackForm.rating),
        feedback: studentFeedbackForm.feedback.trim(),
      });
      setStudentFeedbackForm({ rating: "5", feedback: "" });
      loadStudentFeedbacks();
      await sweetAlert(res.data?.message || "Feedback submitted successfully", { icon: "success" });
    } catch (err) {
      await sweetAlert(err.response?.data?.message || "Feedback submit failed", { icon: "error" });
    } finally {
      setSubmittingStudentFeedback(false);
    }
  };

  const loadStudentFeedbacks = useCallback(async () => {
    if (!student?.id) return;
    try {
      setLoadingStudentFeedbacks(true);
      const res = await axios.get(`${API}/student-feedbacks/${student.id}`, { params: { _ts: Date.now() } });
      setStudentFeedbackRows(Array.isArray(res.data) ? res.data : []);
    } catch (error) {
      setStudentFeedbackRows([]);
    } finally {
      setLoadingStudentFeedbacks(false);
    }
  }, [student?.id]);

  useEffect(() => {
    if (activeSection === "student-feedback-box") {
      loadStudentFeedbacks();
    }
  }, [activeSection, loadStudentFeedbacks]);

  const saveProfile = async () => {
    if (!student?.id) return;

    try {
      setSavingProfile(true);
      const payload = new FormData();
      payload.append("student_name", profileForm.student_name || profile.student_name || "");
      payload.append("phone", profileForm.phone || "");
      payload.append("email", profileForm.email || "");
      payload.append("dob", toDateInputValue(profile.dob));
      payload.append("session_year", profile.session_year || "");
      payload.append("guardian_name", profileForm.guardian_name || "");
      payload.append("address", profileForm.address || "");
      payload.append("course_id", profile.course_id || "");

      await axios.post(`${API}/student-applications/${student.id}`, payload, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      setData((prev) => ({
        ...prev,
        profile: {
          ...prev.profile,
          student_name: profileForm.student_name,
          phone: profileForm.phone,
          email: profileForm.email,
          guardian_name: profileForm.guardian_name,
          address: profileForm.address,
        },
      }));
      setToast("Profile updated successfully");
    } catch (err) {
      setToast(err.response?.data?.message || "Profile update failed");
    } finally {
      setSavingProfile(false);
    }
  };

  const changePassword = async (event) => {
    event.preventDefault();
    if (!student?.id) return;

    try {
      setSavingPassword(true);
      const res = await axios.post(`${API}/student-portal/${student.id}/change-password`, passwordForm);
      setPasswordForm({ current_password: "", new_password: "", new_password_confirmation: "" });
      setToast(res.data?.message || "Password changed successfully");
    } catch (err) {
      setToast(err.response?.data?.message || "Password change failed");
    } finally {
      setSavingPassword(false);
    }
  };

  const printAdmitCard = async () => {
    const design = await fetchAmciDesign("admit");
    printAmciDocument("admit", design, {
      student_name: summary.student_name,
      registration_no: summary.registration_no,
      course_name: summary.course_name,
      session_year: summary.session_year,
      study_centre: summary.study_centre_name,
      status: summary.status,
      student_photo: resolveStudentPhoto(summary?.photo ? summary : profile),
    });
  };

  const printCertificate = async () => {
    const design = await fetchAmciDesign("certificate");
    const summaryMarks = buildMarksSummary(marksSource);
    const gradeRows = await fetchGradePoints();
    const gradeInfo = resolveGradePointByPercentage(summaryMarks.percentage, gradeRows);
    printAmciDocument("certificate", design, {
      student_name: summary.student_name,
      father_name: summary.father_name || profile.guardian_name,
      registration_no: summary.registration_no,
      dob: summary.dob || profile.dob,
      course_name: summary.course_name,
      admission_date: summary.admission_date || profile.admission_date,
      course_completion_date: summary.course_completion_date || profile.course_completion_date,
      study_centre: summary.study_centre_name,
      centre_name: summary.study_centre_name,
      centre_code: summary.study_centre_login,
      centre_address: summary.study_centre_address || summary.centre_address || "",
      session_year: summary.session_year,
      certificate_no: summary.application_no || summary.registration_no,
      issue_date: summary.updated_at || "",
      grade: gradeInfo.grade || summary.status,
      grade_point: gradeInfo.grade_point,
      percentage: `${summaryMarks.percentage}%`,
      total_obtained: summaryMarks.totalObtained,
      total_full_marks: summaryMarks.totalFull,
      verification_qr: summary.registration_no || "Verification QR",
      student_photo: resolveStudentPhoto(summary?.photo ? summary : profile),
    });
  };

  const printMarksheet = async () => {
    const design = await fetchAmciDesign("marksheet");
    const summaryMarks = buildMarksSummary(marks);
    const gradeRows = await fetchGradePoints();
    const gradeInfo = resolveGradePointByPercentage(summaryMarks.percentage, gradeRows);
    printAmciDocument("marksheet", design, {
      student_name: summary.student_name,
      registration_no: summary.registration_no,
      dob: summary.dob || profile.dob,
      course_name: summary.course_name,
      admission_date: summary.admission_date || profile.admission_date,
      course_completion_date: summary.course_completion_date || profile.course_completion_date,
      study_centre: summary.study_centre_name,
      centre_name: summary.study_centre_name,
      centre_code: summary.study_centre_login,
      module_details: buildModuleDetailsRows(marks),
      total_obtained: summaryMarks.totalObtained,
      total_full_marks: summaryMarks.totalFull,
      total_pass_marks: summaryMarks.totalPass,
      obtained_marks: summaryMarks.totalObtained,
      theory_obtained_marks: summaryMarks.theoryObtained,
      theory_full_marks: summaryMarks.theoryFull,
      theory_pass_marks: summaryMarks.theoryPass,
      practical_obtained_marks: summaryMarks.practicalObtained,
      practical_full_marks: summaryMarks.practicalFull,
      practical_pass_marks: summaryMarks.practicalPass,
      project_assignment_obtained_marks: summaryMarks.projectObtained,
      project_assignment_full_marks: summaryMarks.projectFull,
      project_assignment_pass_marks: summaryMarks.projectPass,
      viva_obtained_marks: summaryMarks.vivaObtained,
      viva_full_marks: summaryMarks.vivaFull,
      viva_pass_marks: summaryMarks.vivaPass,
      percentage: `${summaryMarks.percentage}%`,
      grade: gradeInfo.grade,
      issue_date: summary.updated_at || "",
      student_photo: resolveStudentPhoto(summary?.photo ? summary : profile),
    });
  };

  const printIdCard = async () => {
    const design = await fetchAmciDesign("idcard");
    printAmciDocument("idcard", design, {
      student_name: summary.student_name,
      registration_no: summary.registration_no,
      course_name: summary.course_name,
      session_year: summary.session_year,
      study_centre: summary.study_centre_name,
      student_mobile: profile.phone,
      valid_upto: summary.session_year,
      student_photo: resolveStudentPhoto(summary?.photo ? summary : profile),
    });
  };

  const topbarStyle = {
    background: "linear-gradient(120deg, rgba(219,234,254,0.96) 0%, rgba(248,250,252,0.97) 44%, rgba(255,237,213,0.98) 100%)",
    padding: isMobile ? "12px 14px" : "14px 20px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "14px",
    position: "sticky",
    top: 0,
    zIndex: 1400,
    borderBottom: "1px solid rgba(148, 163, 184, 0.18)",
    boxShadow: "0 18px 40px rgba(148, 163, 184, 0.12)",
    flexWrap: "wrap",
    backdropFilter: "blur(12px)",
  };

  const sidebarStyle = {
    width: isMobile ? "280px" : "248px",
    height: "100vh",
    background: "linear-gradient(180deg, #dbeafe 0%, #f8fafc 38%, #fff7ed 100%)",
    color: "#4e1b24",
    position: "fixed",
    top: 0,
    left: 0,
    zIndex: 1500,
    overflowY: "auto",
    overflowX: "hidden",
    paddingBottom: "24px",
    borderRight: "1px solid rgba(148, 163, 184, 0.18)",
    boxShadow: "18px 0 45px rgba(148, 163, 184, 0.18)",
    transform: isMobile ? (mobileMenu ? "translateX(0)" : "translateX(-100%)") : "none",
    transition: "transform 0.28s ease",
    scrollbarWidth: "none",
    msOverflowStyle: "none",
  };

  const contentWrapStyle = {
    flex: 1,
    minHeight: "100vh",
    marginLeft: isMobile ? 0 : "248px",
  };

  const quickMenuCardStyle = {
    ...pageCardStyle,
    padding: "18px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "14px",
    background: "linear-gradient(135deg, rgba(255,255,255,0.96) 0%, rgba(255,247,237,0.92) 100%)",
  };

  const contentGridStyle = {
    display: "grid",
    gridTemplateColumns: isTablet ? "1fr" : "minmax(0, 1.18fr) minmax(320px, 0.82fr)",
    gap: "20px",
    alignItems: "start",
  };

  const renderDashboardContent = () => (
    <div style={{ display: "grid", gap: "20px" }}>
      <div
        style={{
          ...pageCardStyle,
          padding: isMobile ? "20px 18px" : "28px",
          color: "#fff",
          background: "linear-gradient(135deg, #4e1b24 0%, #7c2d3a 52%, #c2410c 100%)",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            position: "absolute",
            inset: "auto -50px -90px auto",
            width: "220px",
            height: "220px",
            borderRadius: "999px",
            background: "radial-gradient(circle, rgba(255,255,255,0.24) 0%, rgba(255,255,255,0) 72%)",
          }}
        />
        <div style={{ position: "relative", display: "grid", gap: "18px", gridTemplateColumns: isTablet ? "1fr" : "minmax(0, 1.2fr) minmax(280px, 0.8fr)" }}>
          <div>
            <p style={{ margin: 0, opacity: 0.82, letterSpacing: "0.14em", textTransform: "uppercase", fontWeight: 800, fontSize: "12px" }}>
              Student Dashboard
            </p>
            <h1 style={{ margin: "12px 0 12px", fontSize: isMobile ? "30px" : "44px", lineHeight: 1.04 }}>
              Welcome back, {summary.student_name || student?.student_name || "Student"}
            </h1>
            {refreshingDashboard ? (
              <span style={{ display: "inline-flex", alignItems: "center", gap: "8px", marginBottom: "12px", padding: "7px 11px", borderRadius: "999px", background: "rgba(255,255,255,0.16)", border: "1px solid rgba(255,255,255,0.2)", fontSize: "12px", fontWeight: 800 }}>
                <i className="bi bi-arrow-repeat"></i>
                Updating latest data
              </span>
            ) : null}
            <p style={{ margin: 0, color: "rgba(255,255,255,0.84)", lineHeight: 1.8, maxWidth: "720px" }}>
              Welcome to your personalized Student Workspace. Designed with the seamless visual style of the Master Admin Dashboard, this hub provides instant access to your profile, examinations, and certifications. Track your achievements, download essential resources, provide feedback, and manage your ID tools—all from one centralized, easy-to-navigate interface.
            </p>
          </div>
          <div style={{ display: "grid", gap: "12px", alignContent: "start" }}>
            <div style={{ background: "rgba(255,255,255,0.14)", border: "1px solid rgba(255,255,255,0.18)", borderRadius: "18px", padding: "16px" }}>
              <strong style={{ display: "block", marginBottom: "6px" }}>Registration No</strong>
              <span>{summary.registration_no || "-"}</span>
            </div>
            <div style={{ background: "rgba(255,255,255,0.14)", border: "1px solid rgba(255,255,255,0.18)", borderRadius: "18px", padding: "16px" }}>
              <strong style={{ display: "block", marginBottom: "6px" }}>Study Centre</strong>
              <span>{summary.study_centre_name || "-"}</span>
            </div>
          </div>
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(4, minmax(0, 1fr))", gap: "16px" }}>
        <StatCard label="Course" value={summary.course_name || "Not Assigned"} icon="bi-journal-richtext" helper="Active student course" />
        <StatCard label="Session" value={summary.session_year || "-"} icon="bi-calendar-check" helper="Current academic session" />
        <StatCard label="Average Marks" value={`${averagePercentage}%`} icon="bi-bar-chart-line" helper="Based on saved marks" />
        <StatCard label="Available Files" value={documents.length} icon="bi-folder2-open" helper="Uploaded student documents" />
      </div>

      <div style={contentGridStyle}>
        <div style={{ display: "grid", gap: "20px" }}>
          <div style={panelStyle}>
            <SectionHeader title="Quick Access" description="The Quick Access section provides direct shortcuts to the core features of your Student Panel, allowing for seamless navigation and efficiency." />
            <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(2, minmax(0, 1fr))", gap: "14px" }}>
              {[
                { label: "Admit Card", icon: "bi-person-badge", key: "admit-card" },
                { label: "Exam Result", icon: "bi-clipboard2-data", key: "exam-result" },
                { label: "Certificate Download", icon: "bi-award", key: "certificate-download" },
                { label: "ID Card Download", icon: "bi-person-vcard", key: "id-card-download" },
              ].map((item) => (
                <button key={item.key} type="button" onClick={() => openSection(item.key)} style={quickMenuCardStyle}>
                  <span>
                    <strong style={{ display: "block", color: "#0f172a" }}>{item.label}</strong>
                    <small style={{ color: "#64748b" }}>Open section</small>
                  </span>
                  <div
                    style={{
                      width: "42px",
                      height: "42px",
                      borderRadius: "14px",
                      background: "linear-gradient(135deg, #7c2d3a 0%, #c2410c 100%)",
                      color: "#fff",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <i className={`bi ${item.icon}`}></i>
                  </div>
                </button>
              ))}
            </div>
          </div>

          <div style={panelStyle}>
            <SectionHeader title="Profile Snapshot" description="Student profile quick view." />
            <DetailRow label="Student Name" value={profile.student_name} compact={isMobile} />
            <DetailRow label="Login ID" value={profile.student_login_id || summary.registration_no} compact={isMobile} />
            <DetailRow label="Phone" value={profile.phone} compact={isMobile} />
            <DetailRow label="Email" value={profile.email} compact={isMobile} />
            <DetailRow label="Address" value={profile.address} compact={isMobile} />
          </div>

          <div style={panelStyle}>
            <SectionHeader title="My Course" description="Manage and track your active learning." />
            <CourseHighlightCard
              course={{
                title: summary.course_name,
                description: summary.course_description,
                thumbnail: summary.course_thumbnail,
                duration: summary.course_duration,
                semester_count: summary.course_semester_count,
              }}
            />
          </div>
        </div>

        <div style={{ display: "grid", gap: "20px" }}>
          <div style={panelStyle}>
            <SectionHeader title="Academic Status" description="Student status and centre reference." />
            <DetailRow label="Study Centre" value={summary.study_centre_name} compact={isMobile} />
            <DetailRow label="Centre Code" value={summary.study_centre_login} compact={isMobile} />
            <DetailRow label="Centre Contact" value={summary.study_centre_contact} compact={isMobile} />
            <DetailRow label="Approved At" value={summary.approved_at} compact={isMobile} />
          </div>

          <div style={panelStyle}>
            <SectionHeader title="Recent Progress" description="Marks & document readiness status." />
            <div style={{ display: "grid", gap: "12px" }}>
              <div style={{ padding: "16px", borderRadius: "18px", background: "#f8fafc", border: "1px solid rgba(148, 163, 184, 0.16)" }}>
                <strong style={{ display: "block", color: "#0f172a" }}>Marks Entries</strong>
                <span style={{ color: "#64748b" }}>{marks.length} row available in your dashboard.</span>
              </div>
              <div style={{ padding: "16px", borderRadius: "18px", background: "#f8fafc", border: "1px solid rgba(148, 163, 184, 0.16)" }}>
                <strong style={{ display: "block", color: "#0f172a" }}>Documents</strong>
                <span style={{ color: "#64748b" }}>{documents.length} uploaded files linked with your profile.</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderStudyMaterial = () => (
    <div style={panelStyle}>
      <SectionHeader title="Study Material" description="বর্তমানে student profile-এর সাথে linked documents এখানে download করা যাবে।" />
      {documents.length === 0 ? (
        <div style={{ color: "#64748b" }}>No study material available yet.</div>
      ) : (
        <div style={{ display: "grid", gap: "12px" }}>
          {documents.map((item) => (
            <a
              key={`${item.label}-${item.name}`}
              href={item.url}
              target="_blank"
              rel="noreferrer"
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: "12px",
                padding: "16px",
                borderRadius: "18px",
                border: "1px solid rgba(148, 163, 184, 0.16)",
                background: "linear-gradient(135deg, rgba(248,250,252,0.96) 0%, rgba(255,237,213,0.76) 100%)",
              }}
            >
              <span>
                <strong style={{ display: "block", color: "#0f172a" }}>{item.label}</strong>
                <small style={{ color: "#64748b" }}>{item.name}</small>
              </span>
              <i className="bi bi-download" style={{ color: "#7c2d3a", fontSize: "18px" }}></i>
            </a>
          ))}
        </div>
      )}
    </div>
  );

  const renderExamCard = () => (
    <div style={panelStyle}>
      <SectionHeader
        title="Admit Card"
        description="Printable admit card student panel থেকেই generate করা যাবে।"
        action={
          <button
            type="button"
            onClick={printAdmitCard}
            style={{
              border: "none",
              borderRadius: "14px",
              padding: "12px 16px",
              fontWeight: 800,
              background: "linear-gradient(135deg, #7c2d3a 0%, #c2410c 100%)",
              color: "#fff",
            }}
          >
            Print Admit Card
          </button>
        }
      />
      <DetailRow label="Student Name" value={summary.student_name} compact={isMobile} />
      <DetailRow label="Registration No" value={summary.registration_no} compact={isMobile} />
      <DetailRow label="Course" value={summary.course_name} compact={isMobile} />
      <DetailRow label="Session" value={summary.session_year} compact={isMobile} />
      <DetailRow label="Study Centre" value={summary.study_centre_name} compact={isMobile} />
    </div>
  );

  const renderOnlineExam = () => (
    <div style={panelStyle}>
      <SectionHeader title="Online Exam" description="Existing front-end online exam module-এ direct jump করা যাবে।" />
      <div style={{ display: "grid", gap: "14px" }}>
        <div style={{ padding: "18px", borderRadius: "18px", background: "#f8fafc", border: "1px solid rgba(148, 163, 184, 0.16)" }}>
          <strong style={{ display: "block", color: "#0f172a", marginBottom: "6px" }}>Exam Access</strong>
          <span style={{ color: "#64748b", lineHeight: 1.7 }}>
            Online exam page খুলতে নিচের button ব্যবহার করুন। Student panel shell unchanged থাকবে।
          </span>
        </div>
        <button
          type="button"
          onClick={() => navigate("/online-exam")}
          style={{
            border: "none",
            borderRadius: "16px",
            padding: "14px 18px",
            fontWeight: 800,
            background: "linear-gradient(135deg, #7c2d3a 0%, #c2410c 100%)",
            color: "#fff",
            width: isMobile ? "100%" : "fit-content",
          }}
        >
          Open Online Exam
        </button>
      </div>
    </div>
  );

  const renderExamResult = () => (
    <div style={panelStyle}>
      <SectionHeader title="Exam Result" description="Saved module marks and approval status এখানে visible থাকবে।" />
      {marks.length === 0 ? (
        <div style={{ color: "#64748b" }}>No exam result available yet.</div>
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table className="table align-middle mb-0">
            <thead>
              <tr>
                <th>Semester</th>
                <th>Module</th>
                <th>Obtained</th>
                <th>Max</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {marks.map((row) => (
                <tr key={`${row.application_id}-${row.module_id}`}>
                  <td>{row.semester_title || `Semester ${row.semester_no || 1}`}</td>
                  <td>{row.module_title}</td>
                  <td>{row.marks_obtained ?? 0}</td>
                  <td>{row.max_marks ?? 100}</td>
                  <td className="text-capitalize">{row.approval_status || "-"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );

  const renderCertificateDownload = () => (
    <div style={panelStyle}>
      <SectionHeader
        title="Certificate Download"
        description="Printable certificate copy student dashboard থেকে generate করতে পারবেন।"
        action={
          <button
            type="button"
            onClick={printCertificate}
            style={{
              border: "none",
              borderRadius: "14px",
              padding: "12px 16px",
              fontWeight: 800,
              background: "linear-gradient(135deg, #7c2d3a 0%, #c2410c 100%)",
              color: "#fff",
            }}
          >
            Print Certificate
          </button>
        }
      />
      <div style={{ display: "grid", gap: "12px" }}>
        <DetailRow label="Student Name" value={summary.student_name} compact={isMobile} />
        <DetailRow label="Registration No" value={summary.registration_no} compact={isMobile} />
        <DetailRow label="Course" value={summary.course_name} compact={isMobile} />
        <DetailRow label="Study Centre" value={summary.study_centre_name} compact={isMobile} />
      </div>
    </div>
  );

  const renderMarksheetDownload = () => (
    <div style={panelStyle}>
      <SectionHeader
        title="Marksheet Download"
        description="Current marks data থেকে marksheet print-ready format-এ generate হবে।"
        action={
          <button
            type="button"
            onClick={printMarksheet}
            style={{
              border: "none",
              borderRadius: "14px",
              padding: "12px 16px",
              fontWeight: 800,
              background: "linear-gradient(135deg, #7c2d3a 0%, #c2410c 100%)",
              color: "#fff",
            }}
          >
            Print Marksheet
          </button>
        }
      />
      <p style={{ margin: 0, color: "#64748b", lineHeight: 1.7 }}>
        Average: <strong style={{ color: "#0f172a" }}>{averagePercentage}%</strong> | Total Module Row:{" "}
        <strong style={{ color: "#0f172a" }}>{marks.length}</strong>
      </p>
    </div>
  );

  const renderAchievement = () => (
    <div style={panelStyle}>
      <SectionHeader title="Achievement" description="Student progress অনুযায়ী dashboard achievements." />
      <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(2, minmax(0, 1fr))", gap: "14px" }}>
        {achievements.map((item) => (
          <div
            key={item.label}
            style={{
              padding: "18px",
              borderRadius: "20px",
              border: "1px solid rgba(148, 163, 184, 0.16)",
              background: item.ready ? "linear-gradient(135deg, rgba(220,252,231,0.92) 0%, rgba(240,253,250,0.94) 100%)" : "linear-gradient(135deg, rgba(248,250,252,0.96) 0%, rgba(255,255,255,0.94) 100%)",
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", gap: "12px", alignItems: "center" }}>
              <div>
                <strong style={{ display: "block", color: "#0f172a", marginBottom: "6px" }}>{item.label}</strong>
                <span style={{ color: "#64748b", lineHeight: 1.6 }}>{item.helper}</span>
              </div>
              <div
                style={{
                  width: "44px",
                  height: "44px",
                  borderRadius: "16px",
                  background: item.ready ? "linear-gradient(135deg, #16a34a 0%, #059669 100%)" : "linear-gradient(135deg, #cbd5e1 0%, #94a3b8 100%)",
                  color: "#fff",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <i className={`bi ${item.icon}`}></i>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderMessageBox = (type) => (
    <div style={panelStyle}>
      <SectionHeader title={type === "Feedback" ? "Send Feedback" : "Send Complain"} description="এই section থেকে master panel-এ message submit করা যাবে।" />
      <div style={{ display: "grid", gap: "14px" }}>
        <textarea
          className="form-control"
          rows="6"
          placeholder={`Write your ${type.toLowerCase()} here`}
          value={feedbackForm.type === type ? feedbackForm.message : ""}
          onChange={(event) => setFeedbackForm({ type, message: event.target.value })}
          style={{ borderRadius: "18px", padding: "16px" }}
        />
        <button
          type="button"
          disabled={submittingMessage}
          onClick={() => submitMessage(type)}
          style={{
            border: "none",
            borderRadius: "16px",
            padding: "14px 18px",
            fontWeight: 800,
            background: "linear-gradient(135deg, #7c2d3a 0%, #c2410c 100%)",
            color: "#fff",
            width: isMobile ? "100%" : "fit-content",
          }}
        >
          {submittingMessage && feedbackForm.type === type ? "Sending..." : `Send ${type}`}
        </button>
      </div>
    </div>
  );

  const renderProfileEdit = () => (
    <div style={panelStyle}>
      <SectionHeader title="Profile Edit Some Data" description="Student নিজের কিছু editable data update করতে পারবে।" />
      <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(2, minmax(0, 1fr))", gap: "14px" }}>
        <div>
          <label className="form-label">Student Name</label>
          <input className="form-control" value={profileForm.student_name} onChange={(e) => setProfileForm((prev) => ({ ...prev, student_name: e.target.value }))} />
        </div>
        <div>
          <label className="form-label">Phone</label>
          <input className="form-control" value={profileForm.phone} onChange={(e) => setProfileForm((prev) => ({ ...prev, phone: e.target.value }))} />
        </div>
        <div>
          <label className="form-label">Email</label>
          <input className="form-control" value={profileForm.email} onChange={(e) => setProfileForm((prev) => ({ ...prev, email: e.target.value }))} />
        </div>
        <div>
          <label className="form-label">Guardian Name</label>
          <input className="form-control" value={profileForm.guardian_name} onChange={(e) => setProfileForm((prev) => ({ ...prev, guardian_name: e.target.value }))} />
        </div>
        <div style={{ gridColumn: isMobile ? "auto" : "1 / -1" }}>
          <label className="form-label">Address</label>
          <textarea className="form-control" rows="4" value={profileForm.address} onChange={(e) => setProfileForm((prev) => ({ ...prev, address: e.target.value }))} />
        </div>
      </div>
      <div style={{ marginTop: "16px" }}>
        <button
          type="button"
          disabled={savingProfile}
          onClick={saveProfile}
          style={{
            border: "none",
            borderRadius: "16px",
            padding: "14px 18px",
            fontWeight: 800,
            background: "linear-gradient(135deg, #7c2d3a 0%, #c2410c 100%)",
            color: "#fff",
          }}
        >
          {savingProfile ? "Saving..." : "Save Profile"}
        </button>
      </div>
    </div>
  );

  const renderChangePassword = () => (
    <form onSubmit={changePassword} style={{ ...panelStyle, display: "grid", gap: "14px" }}>
      <SectionHeader title="Change Password" description="Current password diye nijer student password update korun." />
      <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(3, minmax(0, 1fr))", gap: "14px" }}>
        <div>
          <label className="form-label">Current Password</label>
          <input
            type="password"
            className="form-control"
            value={passwordForm.current_password}
            onChange={(event) => setPasswordForm((prev) => ({ ...prev, current_password: event.target.value }))}
            required
          />
        </div>
        <div>
          <label className="form-label">New Password</label>
          <input
            type="password"
            className="form-control"
            value={passwordForm.new_password}
            onChange={(event) => setPasswordForm((prev) => ({ ...prev, new_password: event.target.value }))}
            required
            minLength={4}
          />
        </div>
        <div>
          <label className="form-label">Confirm New Password</label>
          <input
            type="password"
            className="form-control"
            value={passwordForm.new_password_confirmation}
            onChange={(event) => setPasswordForm((prev) => ({ ...prev, new_password_confirmation: event.target.value }))}
            required
            minLength={4}
          />
        </div>
      </div>
      <div>
        <button
          type="submit"
          disabled={savingPassword}
          style={{
            border: "none",
            borderRadius: "16px",
            padding: "14px 18px",
            fontWeight: 800,
            background: "linear-gradient(135deg, #7c2d3a 0%, #c2410c 100%)",
            color: "#fff",
          }}
        >
          {savingPassword ? "Saving..." : "Save Password"}
        </button>
      </div>
    </form>
  );

  const renderIdCard = () => (
    <div style={panelStyle}>
      <SectionHeader
        title="ID Card Download"
        description="Student ID card printable format-এ generate হবে।"
        action={
          <button
            type="button"
            onClick={printIdCard}
            style={{
              border: "none",
              borderRadius: "14px",
              padding: "12px 16px",
              fontWeight: 800,
              background: "linear-gradient(135deg, #7c2d3a 0%, #c2410c 100%)",
              color: "#fff",
            }}
          >
            Print ID Card
          </button>
        }
      />
      <DetailRow label="Student Name" value={summary.student_name} compact={isMobile} />
      <DetailRow label="Registration No" value={summary.registration_no} compact={isMobile} />
      <DetailRow label="Course" value={summary.course_name} compact={isMobile} />
      <DetailRow label="Phone" value={profile.phone} compact={isMobile} />
      <DetailRow label="Study Centre" value={summary.study_centre_name} compact={isMobile} />
    </div>
  );

  const renderDirectMessage = () => {
    const actor = {
      type: "student",
      id: student?.id,
      name: summary.student_name || student?.student_name || student?.login_id || "Student",
    };

    return <DirectChat actor={actor} title="New Message" />;
  };

  const renderStudentFeedbackBox = () => (
    <div style={{ display: "grid", gap: "18px" }}>
      <div style={panelStyle}>
        <SectionHeader
          title="Feedback Box"
          description="Rating select kore feedback description submit korun. Admin panel theke review, approve ba reject kora hobe."
        />
        <div style={{ display: "grid", gap: "16px", maxWidth: "720px" }}>
          <div>
            <label className="form-label">Rating</label>
            <select
              className="form-select"
              value={studentFeedbackForm.rating}
              onChange={(event) => setStudentFeedbackForm((prev) => ({ ...prev, rating: event.target.value }))}
            >
              <option value="5">5 - Excellent</option>
              <option value="4">4 - Very Good</option>
              <option value="3">3 - Good</option>
              <option value="2">2 - Average</option>
              <option value="1">1 - Poor</option>
            </select>
          </div>
          <div>
            <label className="form-label">Feedback Description</label>
            <textarea
              className="form-control"
              rows="6"
              value={studentFeedbackForm.feedback}
              onChange={(event) => setStudentFeedbackForm((prev) => ({ ...prev, feedback: event.target.value }))}
              placeholder="Write your feedback"
            />
          </div>
          <div>
            <button type="button" className="btn btn-danger px-4" disabled={submittingStudentFeedback} onClick={submitStudentFeedback}>
              {submittingStudentFeedback ? "Submitting..." : "Submit Feedback"}
            </button>
          </div>
        </div>
      </div>

      <div style={panelStyle}>
        <SectionHeader title="My Feedback List" description="আপনি যে feedback submit করেছেন সেগুলো এখানে দেখা যাবে।" />
        {loadingStudentFeedbacks ? <div style={{ color: "#64748b", fontWeight: 700 }}>Loading feedback list...</div> : null}
        {!loadingStudentFeedbacks && studentFeedbackRows.length === 0 ? (
          <div style={{ color: "#64748b" }}>No feedback submitted yet.</div>
        ) : null}
        <div style={{ display: "grid", gap: "12px" }}>
          {studentFeedbackRows.map((item, index) => (
            <div key={item.id} style={{ border: "1px solid rgba(148,163,184,0.16)", borderRadius: "16px", padding: "14px", background: index % 2 === 0 ? "#fff7ed" : "#f8fafc" }}>
              <div style={{ display: "flex", justifyContent: "space-between", gap: "12px", flexWrap: "wrap", marginBottom: "8px" }}>
                <strong style={{ color: "#0f172a" }}>Feedback #{studentFeedbackRows.length - index}</strong>
                <span style={{ padding: "5px 10px", borderRadius: "999px", background: item.status === "approved" ? "#dcfce7" : item.status === "rejected" ? "#fee2e2" : "#fef3c7", color: item.status === "approved" ? "#166534" : item.status === "rejected" ? "#991b1b" : "#92400e", fontWeight: 800, textTransform: "capitalize" }}>
                  {item.status || "pending"}
                </span>
              </div>
              <div style={{ color: "#f59e0b", fontWeight: 900, letterSpacing: "1px", marginBottom: "8px" }}>
                {"★".repeat(Number(item.rating || 0))}{"☆".repeat(5 - Number(item.rating || 0))}
              </div>
              <p style={{ margin: 0, color: "#334155", lineHeight: 1.7 }}>{item.feedback}</p>
              {item.action_remark ? <div style={{ marginTop: "8px", color: "#64748b", fontSize: "13px" }}>Admin remark: {item.action_remark}</div> : null}
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderActiveSection = () => {
    if (loading) {
      return <div style={panelStyle}>Loading student dashboard...</div>;
    }

    switch (activeSection) {
      case "dashboard":
        return renderDashboardContent();
      case "study-material":
        return renderStudyMaterial();
      case "admit-card":
        return renderExamCard();
      case "online-exam":
        return renderOnlineExam();
      case "exam-result":
        return renderExamResult();
      case "certificate-download":
        return renderCertificateDownload();
      case "marksheet-download":
        return renderMarksheetDownload();
      case "achievement":
        return renderAchievement();
      case "send-feedback":
        return renderMessageBox("Feedback");
      case "send-complain":
        return renderMessageBox("Complain");
      case "new-message":
        return renderDirectMessage();
      case "student-feedback-box":
        return renderStudentFeedbackBox();
      case "profile-edit":
        return renderProfileEdit();
      case "change-password":
        return renderChangePassword();
      case "id-card-download":
        return renderIdCard();
      default:
        return renderDashboardContent();
    }
  };

  return (
    <div style={shellStyle}>
      <style>
        {`
          .student-sidebar {
            scrollbar-width: none;
            -ms-overflow-style: none;
          }
          .student-sidebar::-webkit-scrollbar {
            display: none;
            width: 0;
            height: 0;
          }
        `}
      </style>
      {isMobile && mobileMenu ? (
        <div
          onClick={() => setMobileMenu(false)}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(78, 27, 36, 0.22)",
            backdropFilter: "blur(4px)",
            zIndex: 1450,
          }}
        />
      ) : null}

      <div className="student-sidebar" style={sidebarStyle}>
        <div style={{ padding: "20px 16px 18px", borderBottom: "1px solid rgba(148, 163, 184, 0.16)" }}>
          <div
            style={{
              background: "linear-gradient(135deg, rgba(255,255,255,0.72) 0%, rgba(255,237,213,0.88) 100%)",
              border: "1px solid rgba(148, 163, 184, 0.16)",
              borderRadius: "22px",
              padding: "14px 12px",
              textAlign: "center",
              boxShadow: "0 18px 34px rgba(148, 163, 184, 0.14)",
              position: "relative",
            }}
          >
            {isMobile ? (
              <button
                type="button"
                onClick={() => setMobileMenu(false)}
                style={{
                  position: "absolute",
                  top: "10px",
                  right: "10px",
                  width: "34px",
                  height: "34px",
                  borderRadius: "999px",
                  border: "none",
                  background: "linear-gradient(135deg, #7c2d3a 0%, #c2410c 100%)",
                  color: "#fff",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <i className="bi bi-x-lg"></i>
              </button>
            ) : null}
            <img src={logo} alt="NIDCS" style={{ width: "78%", maxWidth: "150px" }} />
            <div style={{ marginTop: "12px" }}>
              <p style={{ margin: 0, fontSize: "11px", letterSpacing: "0.14em", textTransform: "uppercase", color: "rgba(78, 27, 36, 0.64)" }}>
                Student Panel
              </p>
              <p style={{ margin: "4px 0 0", fontSize: "13px", color: "#7c2d3a", fontWeight: 700 }}>Learning Workspace</p>
            </div>
          </div>
        </div>

        <div style={{ marginTop: "12px", padding: "0 10px" }}>
          {menuItems.map((item) => {
            const childActive = item.children?.some((child) => child.key === activeSection);
            const active = item.key === activeSection || childActive;

            return (
              <div key={item.label} style={{ marginBottom: "8px" }}>
                {item.children ? (
                  <>
                    <button
                      type="button"
                      onClick={() => toggleMenu(item.label)}
                      style={{
                        width: "100%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        padding: "12px 14px",
                        cursor: "pointer",
                        margin: "6px 0",
                        borderRadius: "16px",
                        transition: "0.2s ease",
                        background: active ? "linear-gradient(135deg, #7c2d3a 0%, #c2410c 100%)" : "transparent",
                        border: active ? "1px solid rgba(124, 45, 58, 0.18)" : "1px solid transparent",
                        boxShadow: active ? "0 16px 28px rgba(124, 45, 58, 0.20)" : "none",
                      }}
                      onMouseEnter={(e) => {
                        if (!active) {
                          e.currentTarget.style.background = "linear-gradient(135deg, #7c2d3a 0%, #c2410c 100%)";
                          e.currentTarget.style.boxShadow = "0 16px 28px rgba(124, 45, 58, 0.20)";
                          e.currentTarget.style.transform = "translateX(4px)";
                          e.currentTarget.style.border = "1px solid rgba(124, 45, 58, 0.12)";
                          const icon = e.currentTarget.querySelector(".student-sidebar-main-icon");
                          const label = e.currentTarget.querySelector(".student-sidebar-main-label");
                          const arrow = e.currentTarget.querySelector(".student-sidebar-main-arrow");
                          if (icon) icon.style.color = "#ffffff";
                          if (label) label.style.color = "#ffffff";
                          if (arrow) arrow.style.color = "rgba(255,255,255,0.84)";
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!active) {
                          e.currentTarget.style.background = "transparent";
                          e.currentTarget.style.boxShadow = "none";
                          e.currentTarget.style.transform = "translateX(0)";
                          e.currentTarget.style.border = "1px solid transparent";
                          const icon = e.currentTarget.querySelector(".student-sidebar-main-icon");
                          const label = e.currentTarget.querySelector(".student-sidebar-main-label");
                          const arrow = e.currentTarget.querySelector(".student-sidebar-main-arrow");
                          if (icon) icon.style.color = "#7c2d3a";
                          if (label) label.style.color = "#4e1b24";
                          if (arrow) arrow.style.color = "rgba(78, 27, 36, 0.58)";
                        }
                      }}
                    >
                      <span style={{ display: "flex", alignItems: "center", gap: "12px", minWidth: 0 }}>
                        <span
                          style={{
                            width: "34px",
                            height: "34px",
                            borderRadius: "12px",
                            background: active ? "rgba(255,255,255,0.18)" : "rgba(124, 45, 58, 0.08)",
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                          }}
                        >
                          <i className={`bi ${item.icon} student-sidebar-main-icon`} style={{ fontSize: "16px", color: active ? "#ffffff" : "#7c2d3a" }}></i>
                        </span>
                        <span className="student-sidebar-main-label" style={{ fontSize: "14px", fontWeight: 600, color: active ? "#ffffff" : "#4e1b24", whiteSpace: "nowrap" }}>
                          {item.label}
                        </span>
                      </span>
                      <i className={`bi ${openMenus[item.label] ? "bi-chevron-up" : "bi-chevron-down"} student-sidebar-main-arrow`} style={{ color: active ? "rgba(255,255,255,0.84)" : "rgba(78, 27, 36, 0.58)", fontSize: "13px" }}></i>
                    </button>
                    {openMenus[item.label] ? (
                      <div
                        style={{
                          margin: "4px 0 8px 16px",
                          paddingLeft: "16px",
                          borderLeft: "1px solid rgba(124, 45, 58, 0.14)",
                          display: "grid",
                          gap: "4px",
                        }}
                      >
                        {item.children.map((child) => (
                          <button
                            key={child.key}
                            type="button"
                            onClick={() => openSection(child.key)}
                            style={{
                              padding: "10px 12px",
                              cursor: "pointer",
                              fontSize: "13px",
                              borderRadius: "12px",
                              transition: "0.2s ease",
                              background: child.key === activeSection ? "rgba(124, 45, 58, 0.10)" : "transparent",
                              color: child.key === activeSection ? "#7c2d3a" : "#6b3140",
                              marginBottom: "4px",
                              border: child.key === activeSection ? "1px solid rgba(124, 45, 58, 0.14)" : "1px solid transparent",
                              textAlign: "left",
                            }}
                            onMouseEnter={(e) => {
                              if (child.key !== activeSection) {
                                e.currentTarget.style.background = "linear-gradient(135deg, #7c2d3a 0%, #c2410c 100%)";
                                e.currentTarget.style.paddingLeft = "16px";
                                e.currentTarget.style.boxShadow = "0 12px 20px rgba(124, 45, 58, 0.14)";
                                e.currentTarget.style.border = "1px solid rgba(124, 45, 58, 0.12)";
                                e.currentTarget.style.color = "#ffffff";
                              }
                            }}
                            onMouseLeave={(e) => {
                              if (child.key !== activeSection) {
                                e.currentTarget.style.background = "transparent";
                                e.currentTarget.style.paddingLeft = "12px";
                                e.currentTarget.style.boxShadow = "none";
                                e.currentTarget.style.border = "1px solid transparent";
                                e.currentTarget.style.color = "#6b3140";
                              }
                            }}
                          >
                            <span style={{ display: "flex", alignItems: "center", gap: "8px", fontWeight: 500 }}>
                              <i className="bi bi-chevron-right" style={{ fontSize: "11px" }}></i>
                              {child.label}
                            </span>
                          </button>
                        ))}
                      </div>
                    ) : null}
                  </>
                ) : (
                  <button
                    type="button"
                    onClick={() => openSection(item.key)}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      width: "100%",
                      padding: "12px 14px",
                      cursor: "pointer",
                      margin: "6px 0",
                      borderRadius: "16px",
                      transition: "0.2s ease",
                      background: active ? "linear-gradient(135deg, #7c2d3a 0%, #c2410c 100%)" : "transparent",
                      border: active ? "1px solid rgba(124, 45, 58, 0.18)" : "1px solid transparent",
                      boxShadow: active ? "0 16px 28px rgba(124, 45, 58, 0.20)" : "none",
                    }}
                    onMouseEnter={(e) => {
                      if (!active) {
                        e.currentTarget.style.background = "linear-gradient(135deg, #7c2d3a 0%, #c2410c 100%)";
                        e.currentTarget.style.boxShadow = "0 16px 28px rgba(124, 45, 58, 0.20)";
                        e.currentTarget.style.transform = "translateX(4px)";
                        e.currentTarget.style.border = "1px solid rgba(124, 45, 58, 0.12)";
                        const icon = e.currentTarget.querySelector(".student-sidebar-main-icon");
                        const label = e.currentTarget.querySelector(".student-sidebar-main-label");
                        if (icon) icon.style.color = "#ffffff";
                        if (label) label.style.color = "#ffffff";
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!active) {
                        e.currentTarget.style.background = "transparent";
                        e.currentTarget.style.boxShadow = "none";
                        e.currentTarget.style.transform = "translateX(0)";
                        e.currentTarget.style.border = "1px solid transparent";
                        const icon = e.currentTarget.querySelector(".student-sidebar-main-icon");
                        const label = e.currentTarget.querySelector(".student-sidebar-main-label");
                        if (icon) icon.style.color = "#7c2d3a";
                        if (label) label.style.color = "#4e1b24";
                      }
                    }}
                  >
                    <div style={{ display: "flex", gap: "12px", alignItems: "center", minWidth: 0 }}>
                      <div
                        style={{
                          width: "34px",
                          height: "34px",
                          borderRadius: "12px",
                          background: active ? "rgba(255,255,255,0.18)" : "rgba(124, 45, 58, 0.08)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                        }}
                      >
                        <i className={`bi ${item.icon} student-sidebar-main-icon`} style={{ fontSize: "16px", color: active ? "#ffffff" : "#7c2d3a" }}></i>
                      </div>
                      <span className="student-sidebar-main-label" style={{ fontSize: "14px", fontWeight: 600, color: active ? "#ffffff" : "#4e1b24", whiteSpace: "nowrap" }}>
                        {item.label}
                      </span>
                    </div>
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <div style={contentWrapStyle}>
        <div style={topbarStyle}>
          <div style={{ display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap", minWidth: 0 }}>
            {isMobile ? (
              <button
                type="button"
                onClick={() => setMobileMenu(true)}
                style={{
                  width: "40px",
                  height: "40px",
                  borderRadius: "999px",
                  background: "linear-gradient(135deg, #7c2d3a 0%, #c2410c 100%)",
                  border: "none",
                  fontSize: "20px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#fff",
                  boxShadow: "0 14px 24px rgba(124, 45, 58, 0.26)",
                }}
              >
                <i className="bi bi-list"></i>
              </button>
            ) : null}

            <div style={{ minWidth: 0 }}>
              <h4 style={{ margin: "0 0 6px", fontSize: isMobile ? "18px" : "23px", color: "#4e1b24", fontWeight: 800, lineHeight: 1.2 }}>
                National Institute of Digital Careers and Skills
              </h4>
              <div
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "8px",
                  padding: "5px 11px",
                  borderRadius: "999px",
                  background: "rgba(255,255,255,0.72)",
                  border: "1px solid rgba(148, 163, 184, 0.22)",
                  fontSize: "12px",
                  color: "#7c2d3a",
                  maxWidth: "100%",
                }}
              >
                <i className="bi bi-stars"></i>
                <span style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                  Hello {summary.student_name || student?.student_name || "Student"}
                </span>
              </div>
            </div>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: "14px" }}>
            <NotificationBell
              actorType="student"
              actorId={student?.id || 0}
              actorName={summary.student_name || student?.student_name || "Student"}
              onNotificationClick={(item) => {
                if (item?.targetKey) {
                  openSection(item.targetKey);
                }
              }}
            />
            <button
              type="button"
              onClick={refreshData}
              style={{
                width: "40px",
                height: "40px",
                borderRadius: "999px",
                border: "1px solid rgba(148, 163, 184, 0.18)",
                background: "rgba(255,255,255,0.72)",
                color: "#7c2d3a",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <i className="bi bi-arrow-clockwise"></i>
            </button>

            <div ref={profileMenuRef} style={{ position: "relative" }}>
              <button
                type="button"
                onClick={() => setShowProfileMenu((prev) => !prev)}
                style={{
                  width: "42px",
                  height: "42px",
                  borderRadius: "999px",
                  border: "1px solid rgba(148, 163, 184, 0.18)",
                  background: "rgba(255,255,255,0.72)",
                  color: "#7c2d3a",
                  fontWeight: 800,
                }}
              >
                {(summary.student_name || "S").slice(0, 1).toUpperCase()}
              </button>

              {showProfileMenu ? (
                <div
                  style={{
                    position: "absolute",
                    top: "54px",
                    right: 0,
                    width: isMobile ? "240px" : "280px",
                    background: "#fff",
                    borderRadius: "20px",
                    boxShadow: "0 22px 50px rgba(15, 23, 42, 0.18)",
                    border: "1px solid rgba(148, 163, 184, 0.16)",
                    padding: "16px",
                    zIndex: 1600,
                  }}
                >
                  <strong style={{ display: "block", color: "#0f172a", marginBottom: "4px" }}>{summary.student_name || "-"}</strong>
                  <p style={{ margin: 0, color: "#64748b", fontSize: "13px" }}>{summary.registration_no || "-"}</p>
                  <div style={{ display: "grid", gap: "8px", marginTop: "14px" }}>
                    <button
                      type="button"
                      onClick={() => {
                        setShowProfileMenu(false);
                        openSection("profile-edit");
                      }}
                      style={{
                        border: "none",
                        background: "#f8fafc",
                        color: "#0f172a",
                        borderRadius: "14px",
                        padding: "12px 14px",
                        fontWeight: 600,
                        textAlign: "left",
                      }}
                    >
                      Profile Settings
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setShowProfileMenu(false);
                        openSection("change-password");
                      }}
                      style={{
                        border: "none",
                        background: "#f8fafc",
                        color: "#0f172a",
                        borderRadius: "14px",
                        padding: "12px 14px",
                        fontWeight: 600,
                        textAlign: "left",
                      }}
                    >
                      Change Password
                    </button>
                    <button
                      type="button"
                      onClick={logout}
                      style={{
                        border: "none",
                        background: "#fff1f2",
                        color: "#9f1239",
                        borderRadius: "14px",
                        padding: "12px 14px",
                        fontWeight: 700,
                        textAlign: "left",
                      }}
                    >
                      Logout
                    </button>
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        </div>

        <div style={{ padding: isMobile ? "14px" : "24px" }}>
          {error ? <div style={{ ...panelStyle, color: "#991b1b", background: "#fff1f2", marginBottom: "20px" }}>{error}</div> : null}
          {toast ? <div style={{ ...panelStyle, marginBottom: "20px", padding: "16px 18px", color: "#166534", background: "#f0fdf4" }}>{toast}</div> : null}
          {renderActiveSection()}
        </div>
      </div>
    </div>
  );
}

export default StudentDashboard;
